const TERMINAL_STATUSES = new Set([
  'declined',
  'withdrawn',
  'canceled',
  'cancelled',
  'expired',
]);

const FINAL_STATUSES = new Set(['signed', 'finished', 'completed', 'won']);

const SUBMITTED_ALIASES = new Set([
  'submitted',
  'pending',
  'waiting',
  'waiting_response',
  'waiting_owner_response',
  'negotiation',
  'em_negociacao',
]);

const ACCEPTED_ALIASES = new Set(['accepted', 'approved']);

const DECLINED_ALIASES = new Set([
  'declined',
  'rejected',
  'refused',
  'owner_rejected',
]);

const WITHDRAWN_ALIASES = new Set([
  'withdrawn',
  'terminated',
  'tenant_rejected',
  'gave_up',
]);

const CANCELED_ALIASES = new Set([
  'canceled',
  'cancelled',
  'cancelada',
  'cancelado',
]);

const EXPIRED_ALIASES = new Set(['expired', 'expirada', 'expirado']);

const ACTION_KEYS = Object.freeze({
  ACCEPT: 'accept',
  DECLINE: 'decline',
  WITHDRAW: 'withdraw',
  COUNTER: 'counter',
});

const ACTION_PERMISSION_ALIASES = Object.freeze({
  [ACTION_KEYS.ACCEPT]: [
    'accept',
    'accepted',
    'accept_offer',
    'can_accept',
    'canAccept',
  ],
  [ACTION_KEYS.DECLINE]: [
    'decline',
    'reject',
    'refuse',
    'decline_offer',
    'can_decline',
    'canDecline',
  ],
  [ACTION_KEYS.WITHDRAW]: [
    'withdraw',
    'cancel',
    'withdraw_offer',
    'can_withdraw',
    'canWithdraw',
  ],
  [ACTION_KEYS.COUNTER]: [
    'counter',
    'counter_offer',
    'counteroffer',
    'can_counter',
    'canCounter',
  ],
});

const normalizedText = value =>
  String(value || '')
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[-\s]+/g, '_')
    .toLowerCase();

const firstPresent = values =>
  values.find(value => value !== undefined && value !== null && value !== '');

const asArray = value => (Array.isArray(value) ? value : []);

const objectValues = object =>
  object && typeof object === 'object' && !Array.isArray(object)
    ? Object.values(object)
    : [];

const offerStatusValue = offer =>
  typeof offer === 'string'
    ? offer
    : firstPresent([
        offer?.status,
        offer?.status_key,
        offer?.statusKey,
        offer?.status_v3,
        offer?.macro_status,
        offer?.macroStatus,
        offer?.summary?.status,
      ]);

export const normalizeOfferWorkflowStatus = offer => {
  const status = normalizedText(offerStatusValue(offer));
  if (SUBMITTED_ALIASES.has(status)) return 'submitted';
  if (ACCEPTED_ALIASES.has(status)) return 'accepted';
  if (DECLINED_ALIASES.has(status)) return 'declined';
  if (WITHDRAWN_ALIASES.has(status)) return 'withdrawn';
  if (CANCELED_ALIASES.has(status)) return 'canceled';
  if (EXPIRED_ALIASES.has(status)) return 'expired';
  if (FINAL_STATUSES.has(status)) return status;
  return status || 'unknown';
};

const currentRevision = offer =>
  offer?.current_revision ||
  offer?.currentRevision ||
  offer?.negotiation_timeline?.current_revision ||
  offer?.negotiationTimeline?.currentRevision ||
  null;

export const currentOfferRevisionId = offer =>
  firstPresent([
    currentRevision(offer)?.id,
    offer?.current_revision_id,
    offer?.currentRevisionId,
    offer?.metadata?.current_revision_id,
  ]) ?? null;

const currentRevisionPending = offer =>
  currentRevision(offer)?.pending !== false;

const revisionExpiresAt = offer =>
  firstPresent([
    currentRevision(offer)?.expires_at,
    currentRevision(offer)?.expiresAt,
    offer?.metadata?.current_revision_expires_at,
    offer?.current_revision_expires_at,
  ]);

export const isOfferRevisionExpired = (offer, now = new Date()) => {
  const expiresAt = revisionExpiresAt(offer);
  if (!expiresAt) return false;

  const parsedExpiry = new Date(expiresAt).getTime();
  const parsedNow =
    now instanceof Date ? now.getTime() : new Date(now).getTime();
  if (Number.isNaN(parsedExpiry) || Number.isNaN(parsedNow)) return false;
  return parsedExpiry <= parsedNow;
};

const hasContract = offer =>
  Boolean(
    offer?.ccv_contract ||
      offer?.ccv ||
      asArray(offer?.property_contracts).length
  );

const hasDocumentation = offer =>
  Boolean(
    offer?.documentation_status ||
      offer?.kyc_status ||
      offer?.kyc_verification ||
      asArray(offer?.kyc_verifications).length
  );

const normalizedContractStatus = offer =>
  normalizedText(
    offer?.ccv_contract?.status ||
      offer?.ccv?.status ||
      asArray(offer?.property_contracts)[0]?.status
  );

const explicitPermissionValue = (source, aliases) => {
  if (!source) return undefined;

  if (Array.isArray(source)) {
    const normalizedItems = source.map(item =>
      normalizedText(typeof item === 'string' ? item : item?.key || item?.name)
    );
    return aliases.some(alias =>
      normalizedItems.includes(normalizedText(alias))
    );
  }

  if (typeof source !== 'object') return undefined;

  const matchedKey = aliases.find(alias =>
    Object.prototype.hasOwnProperty.call(source, alias)
  );
  if (matchedKey) return Boolean(source[matchedKey]);

  const nested = objectValues(source)
    .map(value => explicitPermissionValue(value, aliases))
    .find(value => value !== undefined);

  return nested;
};

const actionPermission = (offer, action) => {
  const aliases = ACTION_PERMISSION_ALIASES[action] || [action];
  const sources = [
    offer?.actions,
    offer?.available_actions,
    offer?.availableActions,
    offer?.permissions,
    offer?.action_permissions,
    offer?.actionPermissions,
    offer?.capabilities,
    offer?.meta?.actions,
    offer?.meta?.permissions,
    offer?.metadata?.actions,
    offer?.metadata?.permissions,
  ];

  return sources
    .map(source => explicitPermissionValue(source, aliases))
    .find(value => value !== undefined);
};

const canRunAction = (offer, action) =>
  actionPermission(offer, action) !== false;

const submittedBlockedReason = ({ hasRevision, pending, expired }) => {
  if (!hasRevision) {
    return 'A rodada vigente da proposta não está disponível; recarregue antes de decidir.';
  }
  if (!pending) {
    return 'A rodada vigente já foi decidida; recarregue para ver o status atual.';
  }
  if (expired) {
    return 'O prazo da rodada vigente expirou; abra a proposta para revisar antes de agir.';
  }
  return '';
};

const acceptedBlockedReason = offer => {
  const contractStatus = normalizedContractStatus(offer);
  if (['signed', 'active'].includes(contractStatus)) {
    return 'Oferta aceita e contrato assinado; acompanhe fechamento, parcelas e pós-contrato.';
  }
  if (hasContract(offer)) {
    return 'Oferta aceita; acompanhe o contrato digital e as assinaturas.';
  }
  if (hasDocumentation(offer)) {
    return 'Oferta aceita; acompanhe documentação, KYC e reserva antes do contrato.';
  }
  return 'Oferta aceita; siga pelos próximos passos de reserva, documentação e contrato.';
};

const terminalBlockedReason = status => {
  if (status === 'declined')
    return 'Oferta recusada; não há próxima decisão ativa.';
  if (status === 'withdrawn')
    return 'Oferta retirada; não há próxima decisão ativa.';
  if (status === 'expired')
    return 'Oferta expirada; não há próxima decisão ativa.';
  return 'Oferta cancelada; não há próxima decisão ativa.';
};

export const offerDecisionState = (offer, { now = new Date() } = {}) => {
  const status = normalizeOfferWorkflowStatus(offer);
  const submitted = status === 'submitted';
  const accepted = status === 'accepted';
  const terminal = TERMINAL_STATUSES.has(status);
  const final = FINAL_STATUSES.has(status);
  const hasRevision = currentOfferRevisionId(offer) !== null;
  const pending = currentRevisionPending(offer);
  const expired = submitted && isOfferRevisionExpired(offer, now);
  const actionReady = submitted && hasRevision && pending && !expired;

  const canAccept = actionReady && canRunAction(offer, ACTION_KEYS.ACCEPT);
  const canDecline = actionReady && canRunAction(offer, ACTION_KEYS.DECLINE);
  const canWithdraw = actionReady && canRunAction(offer, ACTION_KEYS.WITHDRAW);
  const canCounter = actionReady && canRunAction(offer, ACTION_KEYS.COUNTER);

  let blockedReason = '';
  if (submitted) {
    blockedReason = submittedBlockedReason({ hasRevision, pending, expired });
    if (
      !blockedReason &&
      ![canAccept, canDecline, canWithdraw, canCounter].some(Boolean)
    ) {
      blockedReason = 'Seu perfil não tem permissão para decidir esta oferta.';
    }
  } else if (accepted) {
    blockedReason = acceptedBlockedReason(offer);
  } else if (terminal) {
    blockedReason = terminalBlockedReason(status);
  } else if (final) {
    blockedReason = 'Oferta finalizada; acompanhe o histórico e os documentos.';
  } else {
    blockedReason =
      'Status da oferta não reconhecido; ações de decisão foram bloqueadas.';
  }

  return {
    status,
    accepted,
    terminal,
    final,
    hasCurrentRevision: hasRevision,
    currentRevisionPending: pending,
    expired,
    canAccept,
    canDecline,
    canWithdraw,
    canCounter,
    blockedReason,
    hasMutableAction: [canAccept, canDecline, canWithdraw, canCounter].some(
      Boolean
    ),
  };
};

// The primary CTA depends on the decision state, which in turn depends on the
// current revision deadline. The clock is injected (same `{ now }` contract as
// `offerDecisionState`/`isOfferRevisionExpired`) so callers and specs can pin a
// deterministic instant instead of silently reading the wall clock.
export const offerCardPrimaryAction = (
  offer,
  phase = 'negotiation',
  { now = new Date() } = {}
) => {
  const state = offerDecisionState(offer, { now });
  const normalizedPhase = normalizedText(phase);

  if (state.status === 'submitted') {
    return {
      key: 'open_step',
      label: state.hasMutableAction ? 'Responder proposta' : 'Abrir negociação',
      icon: state.expired ? 'i-lucide-clock-alert' : 'i-lucide-chevron-right',
      tone: 'primary',
    };
  }

  if (state.accepted) {
    if (normalizedPhase === 'contract' || hasContract(offer)) {
      return {
        key: 'contract',
        label: 'Abrir contrato',
        icon: 'i-lucide-file-check-2',
        tone: 'primary',
      };
    }
    if (normalizedPhase === 'documentation' || hasDocumentation(offer)) {
      return {
        key: 'kyc',
        label: 'Abrir documentação',
        icon: 'i-lucide-shield-check',
        tone: 'primary',
      };
    }
    return {
      key: 'open_step',
      label: 'Ver próximos passos',
      icon: 'i-lucide-list-checks',
      tone: 'primary',
    };
  }

  if (state.final) {
    return {
      key: 'contract',
      label: 'Ver contrato assinado',
      icon: 'i-lucide-file-check-2',
      tone: 'primary',
    };
  }

  if (state.terminal) {
    return {
      key: 'open_step',
      label: 'Ver motivo',
      icon: 'i-lucide-circle-x',
      tone: 'primary',
    };
  }

  return {
    key: 'open_step',
    label: 'Abrir detalhes',
    icon: 'i-lucide-file-search',
    tone: 'primary',
  };
};

export { ACTION_KEYS as OFFER_ACTION_KEYS };
